import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { USER_ATTRS_FOR_CASE_TRANSFORM } from 'Users';
import { transformKeyCase } from 'Utils/transformKeyCase';
import { ALL_RECORDS, GRADEBOOK_STATUSES } from './constants';
import {
    type CohortExams,
    type ExamScoreDetails,
    type GradebookExam,
    type GradebookExamProgress,
    type GradebookStudent,
    type LessonProgressRecord,
} from './AdminCohortGradebook.types';

/*
    The slice keeps the server's snake_case. The grading modal's adapter and the cohort's
    learner_projects meta both expect it, and lesson_progress.challenge_scores is keyed by
    challenge id, which a casing conversion would rewrite. So request bodies go out in snake_case
    too. GET params are snake-cased by the base query in any case.
*/
const baseConfig = getBackRoyalApiBaseConfig('adminGradebookApi', { convertCasing: false });

// The shared base query retries a disconnect and a 502/504. Every write here should run once.
const NO_RETRY = { maxRetries: 0 };

type StudentsIndexResponse = { contents: { users?: GradebookStudent[] } };
type PlaylistsIndexResponse = {
    contents: { playlists?: { locale_pack: { id: string }; stream_entries?: { locale_pack_id: string }[] }[] };
};
type StreamsIndexResponse = { contents: { lesson_streams?: GradebookExam[] } };
type StreamProgressIndexResponse = {
    contents: { lesson_streams_progress?: GradebookExamProgress[] };
    meta?: { lesson_progress?: LessonProgressRecord[] };
};
type BatchUpdateScoresResponse = { contents: { exam_progress?: GradebookExamProgress[] } };

type ExamsQueryArgs = {
    cohortId: string;
    playlistPackIds: string[];
    requiredStreamPackIds: string[];
    specializationPlaylistPackIds: string[];
};

const unique = <T>(values: T[]) => [...new Set(values)];

/*
    The Users helpers read the AOI records in camelCase. The Iguana User camel-cases the same keys
    in its copyAttrs callback, and everything else stays snake_case.
*/
function camelCaseAoiKeys(user: GradebookStudent) {
    return transformKeyCase<GradebookStudent>(user, { to: 'camelCase', keys: USER_ATTRS_FOR_CASE_TRANSFORM });
}

export const adminGradebookApi = createApi('adminGradebookApi', {
    ...baseConfig,
    // oxlint-disable-next-line max-lines-per-function -- one object literal of endpoints
    endpoints: builder => ({
        // A POST, because the filters are too long for a query string.
        getGradebookStudents: builder.query<GradebookStudent[], { cohortId: string }>({
            query: ({ cohortId }) => ({
                method: 'POST',
                url: 'users/index.json',
                body: {
                    view: 'cohort_gradebook',
                    per_page: ALL_RECORDS,
                    filters: { program_rows: [{ cohort_id: cohortId, status: GRADEBOOK_STATUSES }] },
                },
            }),
            transformResponse: (response: StudentsIndexResponse) =>
                (response.contents.users || []).map(camelCaseAoiKeys),
        }),

        /*
            The exams in the cohort's playlists and in its schedule, split the way the Angular
            gradebook split them. The params match the Iguana Playlist.index and Stream.index calls,
            so the responses carry the same fields.
        */
        getCohortExams: builder.query<CohortExams, ExamsQueryArgs>({
            queryFn: async (
                { playlistPackIds, requiredStreamPackIds, specializationPlaylistPackIds },
                _api,
                _options,
                baseQuery,
            ) => {
                const playlistsResponse = await baseQuery({
                    url: 'playlists.json',
                    params: { filters: { locale_pack_id: playlistPackIds } },
                });
                if (playlistsResponse.error) return { error: playlistsResponse.error };

                const playlists = (playlistsResponse.data as PlaylistsIndexResponse).contents.playlists || [];
                const streamPackIdsFor = (playlist: (typeof playlists)[number]) =>
                    (playlist.stream_entries || []).map(entry => entry.locale_pack_id);

                const streamsResponse = await baseQuery({
                    method: 'POST',
                    url: 'lesson_streams/index.json',
                    body: {
                        filters: {
                            exam: true,
                            locale: 'en',
                            locale_pack_id: unique([...playlists.flatMap(streamPackIdsFor), ...requiredStreamPackIds]),
                        },
                    },
                });
                if (streamsResponse.error) return { error: streamsResponse.error };

                const exams = (streamsResponse.data as StreamsIndexResponse).contents.lesson_streams || [];
                const examsByPackId = new Map(exams.map(exam => [exam.locale_pack.id, exam]));
                const playlistsByPackId = new Map(playlists.map(playlist => [playlist.locale_pack.id, playlist]));
                const examsFor = (packIds: string[]) =>
                    packIds.map(id => examsByPackId.get(id)).filter((exam): exam is GradebookExam => !!exam);

                return {
                    data: {
                        requiredExams: examsFor(requiredStreamPackIds),
                        specializationExams: examsFor(
                            specializationPlaylistPackIds
                                .map(id => playlistsByPackId.get(id))
                                .filter(playlist => !!playlist)
                                .flatMap(streamPackIdsFor),
                        ),
                    },
                };
            },
        }),

        updateHonorsIneligible: builder.mutation<void, { programInclusionId: string; honorsIneligible: boolean }>({
            query: ({ programInclusionId, honorsIneligible }) => ({
                method: 'PUT',
                url: `program_inclusions/${programInclusionId}/update_honors_ineligible.json`,
                body: { record: { honors_ineligible: honorsIneligible } },
            }),
            extraOptions: NO_RETRY,
        }),

        /*
            The lesson scores behind one exam score. The Angular modal read them through
            user.progress.getAllProgress(), which also wrote the student's progress into the admin's
            own local store. This reads the same endpoint and keeps nothing.
        */
        getExamScoreDetails: builder.query<ExamScoreDetails, { userId: string; exam: GradebookExam }>({
            queryFn: async ({ userId, exam: scheduledExam }, _api, _options, baseQuery) => {
                const progressResponse = await baseQuery({
                    url: 'lesson_streams_progress.json',
                    params: { filters: { user_id: userId }, include_lesson_progress: true },
                });
                if (progressResponse.error) return { error: progressResponse.error };

                const { contents, meta } = progressResponse.data as StreamProgressIndexResponse;
                const streamProgress = contents.lesson_streams_progress || [];
                const findProgress = (target: GradebookExam) =>
                    streamProgress.find(progress => progress.locale_pack_id === target.locale_pack.id);

                let exam = scheduledExam;
                let examProgress = findProgress(exam);

                /*
                    A waived exam carries the score of the stream the student originally completed.
                    The server applies an adjustment to every progress record with the same
                    requirement_identifier, so the lessons to adjust are that stream's lessons.
                */
                if (examProgress?.waiver) {
                    const originalResponse = await baseQuery({
                        url: 'lesson_streams/get_originally_completed_stream_for_user.json',
                        params: { user_id: userId, requirement_identifier: exam.locale_pack.requirement_identifier },
                    });
                    if (originalResponse.error) return { error: originalResponse.error };

                    const originalExam = (originalResponse.data as StreamsIndexResponse).contents.lesson_streams?.[0];
                    if (originalExam) {
                        exam = originalExam;
                        examProgress = findProgress(exam);
                    }
                }

                if (!examProgress) {
                    return { error: { status: 'CUSTOM_ERROR', error: 'No progress record for this exam' } };
                }

                return {
                    data: {
                        exam,
                        streamProgressId: examProgress.id,
                        lessonProgressByLocalePackId: Object.fromEntries(
                            (meta?.lesson_progress || []).map(record => [record.locale_pack_id, record]),
                        ),
                    },
                };
            },
            keepUnusedDataFor: 0,
        }),

        batchUpdateExamScores: builder.mutation<
            GradebookExamProgress[],
            { records: { id: string; best_score: number; adjustment_points: number }[]; streamProgressId: string }
        >({
            query: ({ records, streamProgressId }) => ({
                method: 'PUT',
                url: 'lesson_progress/batch_update_scores.json',
                body: { records, stream_progress_id: streamProgressId },
            }),
            transformResponse: (response: BatchUpdateScoresResponse) => response.contents.exam_progress || [],
            extraOptions: NO_RETRY,
        }),
    }),
});
